import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StudyStayLogo from '../components/StudyStayLogo';
import PublicNavbar from '../components/PublicNavbar';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { SUBSCRIPTION_PLANS } from '../services/payment.service';
import {
  Building2,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Shield,
  BarChart3,
  MessageCircle,
  Calendar,
  FileText,
  Euro,
  Sparkles,
  Gift,
  Phone,
  Mail,
  Users,
  Zap,
  Clock,
  ChevronRight,
  Send,
  Home,
  Link2,
  Rocket,
} from 'lucide-react';
import toast from 'react-hot-toast';

const AUDIENCE_MAP = {
  listing_only_private: 'private',
  listing_only_agency: 'agency',
  pro: 'private',
  agency: 'agency',
};

const pricingPlans = SUBSCRIPTION_PLANS
  .filter((p) => p.id !== 'starter' && p.id !== 'listing_only_private' && p.id !== 'listing_only_agency')
  .map((p) => ({
    id: p.id,
    audience: AUDIENCE_MAP[p.id] || 'private',
    name: p.name,
    subtitle: AUDIENCE_MAP[p.id] === 'agency' ? 'Voor immokantoren' : 'Voor particuliere verhuurders',
    price: p.price,
    period: p.period,
    features: p.features,
    notIncluded: p.notIncluded || [],
    popular: p.popular,
  }));

export default function VerhuurderInfo() {
  const [isVisible, setIsVisible] = useState(false);
  const [pricingAudience, setPricingAudience] = useState('private');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    landlordType: 'particulier',
    propertyCount: '',
    preferredDate: '',
    message: '',
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const fullName = formData.fullName.trim();
    const email = formData.email.trim().toLowerCase();
    if (!fullName || !email) {
      toast.error('Vul minimaal je naam en e-mailadres in.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Voer een geldig e-mailadres in.');
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'onboardingAppointments'), {
        fullName,
        email,
        phone: formData.phone.trim(),
        landlordType: formData.landlordType,
        propertyCount: formData.propertyCount,
        preferredDate: formData.preferredDate,
        message: formData.message.trim(),
        status: 'pending',
        source: 'verhuurder-info-page',
        createdAt: serverTimestamp(),
      });

      toast.success('Bedankt! We nemen zo snel mogelijk contact met je op.');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        landlordType: 'particulier',
        propertyCount: '',
        preferredDate: '',
        message: '',
      });
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Kon aanvraag niet verzenden. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  const visiblePlans = pricingPlans.filter((p) => p.audience === pricingAudience);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <PublicNavbar />

      {/* ─── HERO ─── */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 70%)' }} />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 70%)' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-100 text-sm font-medium text-amber-700 mb-8">
              <Building2 className="w-4 h-4" />
              Voor verhuurders & immokantoren
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
              Verhuur je kot{' '}
              <span className="relative">
                <span className="relative z-10 bg-gradient-to-r from-[#5EABEB] to-[#2B6FA6] bg-clip-text text-transparent">
                  eenvoudig & professioneel
                </span>
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              Bereik duizenden studenten, beheer bezichtigingen, genereer contracten en communiceer — alles vanuit één overzichtelijk platform.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register"
                className="group inline-flex items-center justify-center px-8 py-4 text-white font-semibold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full sm:w-auto"
                style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                4 maanden gratis proberen
                <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="#afspraak"
                className="group inline-flex items-center justify-center px-8 py-4 bg-white text-slate-800 font-semibold text-lg rounded-2xl shadow-sm border-2 border-slate-200 hover:border-[#5EABEB] hover:shadow-md transition-all duration-300 hover:-translate-y-1 w-full sm:w-auto"
              >
                <Calendar className="w-5 h-5 mr-2 text-[#5EABEB]" />
                Boek een afspraak
                <ChevronRight className="w-5 h-5 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 4 MAANDEN GRATIS TRIAL BANNER ─── */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-8 sm:p-10">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-100/40 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Gift className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  4 maanden gratis uitproberen
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Start vandaag met StudyStay Pro en probeer alle features 4 maanden gratis. Geen betaling vooraf — annuleer op elk moment.
                </p>
              </div>
              <Link
                to="/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register"
                className="flex-shrink-0 inline-flex items-center px-6 py-3 font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                Start gratis trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── VOOR WIE IS STUDYSTAY? ─── */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-sm font-medium text-[#2B6FA6] mb-6">
              <Users className="w-4 h-4" />
              Voor elk type verhuurder
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Voor wie is StudyStay?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Of je nu één kot verhuurt of een grote portefeuille beheert — StudyStay past zich aan jouw noden aan.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Kleine particulier */}
            <div className="relative bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#5EABEB] to-[#2B6FA6]" />
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Home className="w-7 h-7 text-[#5EABEB]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Kleine particulier</h3>
                    <p className="text-sm text-slate-500">Je verhuurt 1 tot 5 koten</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Ideaal voor ouders, investeerders of eigenaars die een beperkt aantal studentenkamers verhuren. Geen technische kennis nodig — alles werkt intuïtief.
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    'Eenvoudig panden toevoegen met foto\'s',
                    'Direct communiceren met studenten via chat',
                    'Contracten digitaal opstellen & ondertekenen',
                    'Bezichtigingen plannen met één klik',
                    'Betaalbaar startplan — 4 maanden gratis proberen',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B6FA6] hover:text-[#1E5A8A] transition-colors"
                >
                  Start als particulier
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Grote portefeuille */}
            <div className="relative bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#2B6FA6] to-[#5EABEB]" />
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-7 h-7 text-[#2B6FA6]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Meerdere panden</h3>
                    <p className="text-sm text-slate-500">Je verhuurt 5+ studentenkamers</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Perfect voor ervaren verhuurders met een grotere portefeuille. Beheer al je panden, contracten en huurders vanuit één overzichtelijk dashboard.
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    'Onbeperkt panden publiceren met Pro',
                    'Overzichtelijk dashboard met al je listings',
                    'Statistieken per pand — views, interesse & meer',
                    'Alle contracten & bezichtigingen centraal beheerd',
                    'CRM integratie voor import uit externe systemen',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#5EABEB] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#2B6FA6] hover:text-[#1E5A8A] transition-colors"
                >
                  Start met meerdere panden
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WAT BIEDT STUDYSTAY? ─── */}
      <section className="py-20 sm:py-24" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-100 text-sm font-medium text-amber-700 mb-6">
              <Sparkles className="w-4 h-4" />
              Ons platform
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Wat biedt StudyStay voor verhuurders?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Eén platform voor je volledige verhuurproces — van listing tot getekend contract en alles daartussen.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Building2,
                title: 'Panden publiceren',
                description: 'Voeg je koten toe met foto\'s, beschrijving, prijs en locatie. Bereik duizenden studenten direct.',
                color: 'text-[#5EABEB]',
                bg: 'bg-blue-50',
              },
              {
                icon: Calendar,
                title: 'Bezichtigingen plannen',
                description: 'Stel beschikbare tijdslots in en ontvang aanvragen. Studenten boeken zelf een moment.',
                color: 'text-orange-500',
                bg: 'bg-orange-50',
              },
              {
                icon: FileText,
                title: 'Digitale contracten',
                description: 'Maak, verstuur en onderteken huurcontracten volledig digitaal. Met audit trail en PDF export.',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50',
              },
              {
                icon: MessageCircle,
                title: 'In-app berichten',
                description: 'Communiceer direct met geïnteresseerde studenten. Alles in één overzichtelijke inbox.',
                color: 'text-cyan-600',
                bg: 'bg-cyan-50',
              },
              {
                icon: BarChart3,
                title: 'Statistieken & inzichten',
                description: 'Bekijk views, interesse en bezettingsgraad. Optimaliseer je verhuurstrategie met data.',
                color: 'text-pink-600',
                bg: 'bg-pink-50',
              },
              {
                icon: Link2,
                title: 'CRM integraties',
                description: 'Koppel StudyStay met je bestaande beheersystemen. Importeer en synchroniseer automatisch.',
                color: 'text-violet-600',
                bg: 'bg-violet-50',
              },
              {
                icon: Rocket,
                title: 'Boost je listing',
                description: 'Vergroot je zichtbaarheid bij studenten met een betaalde boost. Meer views, sneller verhuurd.',
                color: 'text-amber-500',
                bg: 'bg-amber-50',
              },
              {
                icon: Shield,
                title: 'Veilig & geverifieerd',
                description: 'Geverifieerde profielen, veilige opslag en GDPR-compliant. Je data is in goede handen.',
                color: 'text-slate-700',
                bg: 'bg-slate-100',
              },
            ].map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-2xl p-6 border border-slate-200/80 hover:border-[#5EABEB]/30 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-12 h-12 rounded-xl ${service.bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${service.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          {/* Immokantoor coming soon teaser */}
          <div className="mt-12 max-w-3xl mx-auto">
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-6 text-center">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-800">Immokantoor of agency?</span>{' '}
                We werken aan speciale tools voor professioneel vastgoedbeheer met team-functies en klantenbeheer.{' '}
                <a href="#afspraak" className="font-semibold text-[#2B6FA6] hover:underline">Neem contact op</a> om op de hoogte te blijven.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="py-20 sm:py-24" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Transparante prijzen</h2>
            <p className="text-lg text-slate-600 mb-2">
              Kies het plan dat bij jou past.
            </p>

            <div className="mt-6 inline-flex items-center rounded-xl bg-white border border-slate-200 p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setPricingAudience('private')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  pricingAudience === 'private'
                    ? 'bg-[#5EABEB] text-white shadow-sm'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Particulier
              </button>
              <button
                type="button"
                onClick={() => setPricingAudience('agency')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  pricingAudience === 'agency'
                    ? 'bg-[#5EABEB] text-white shadow-sm'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Immokantoor
              </button>
            </div>
          </div>

          {pricingAudience === 'private' ? (
            <>
              {/* Listing-Only gratis card */}
              <div className="mb-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50/60 via-white to-emerald-50/60 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-xl font-bold text-slate-900">Listing-Only</h3>
                      <span className="px-3 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">100% gratis</span>
                    </div>
                    <p className="text-sm text-slate-600">Onbeperkt panden publiceren — contact via e-mail</p>
                  </div>
                  <Link
                    to="/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register&plan=listing_only_private"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    Start gratis
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Paid plans */}
              <div className="grid md:grid-cols-2 gap-6">
                {visiblePlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative bg-white rounded-2xl p-8 shadow-lg border transition-all duration-300 hover:shadow-xl ${
                      plan.popular ? 'border-[#5EABEB] ring-1 ring-[#5EABEB]/20' : 'border-slate-200'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="px-4 py-1 rounded-full text-xs font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}>
                          MEEST GEKOZEN
                        </span>
                      </div>
                    )}

                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                      <p className="text-sm text-slate-500">{plan.subtitle}</p>
                    </div>

                    <div className="mb-6">
                      {plan.price === 0 ? (
                        <div className="flex items-end gap-2">
                          <span className="text-4xl font-bold text-emerald-600">Gratis</span>
                        </div>
                      ) : (
                        <div className="flex items-end gap-2">
                          <span className="text-4xl font-bold text-slate-900 flex items-center">
                            <Euro className="w-6 h-6 text-[#5EABEB] mr-1" />
                            {plan.price}
                          </span>
                          <span className="text-sm text-slate-500 pb-1">/{plan.period?.replace('per ', '')}</span>
                        </div>
                      )}
                      {plan.id === 'pro' && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold">30 dagen gratis</span>
                        </div>
                      )}
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center text-sm text-slate-700">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mr-2.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                      {plan.notIncluded.map((f, i) => (
                        <li key={`no-${i}`} className="flex items-center text-sm text-slate-400">
                          <XCircle className="w-4 h-4 text-slate-300 mr-2.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={`/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register&plan=${plan.id}`}
                      className={`block w-full text-center py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                        plan.popular
                          ? 'text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'
                          : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                      }`}
                      style={plan.popular ? { background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' } : undefined}
                    >
                      Kies {plan.name.split(' ')[0]}
                    </Link>
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* Immokantoor — Coming Soon */
            <div className="max-w-2xl mx-auto rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Coming soon voor immokantoren</h3>
              <p className="text-slate-600 mb-6 leading-relaxed max-w-md mx-auto">
                We werken aan speciale plannen voor immokantoren met een volledig agency dashboard, team workflows en client management. Wil je op de hoogte blijven?
              </p>
              <a
                href="#afspraak"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
              >
                <Calendar className="w-4 h-4" />
                Boek een gesprek
              </a>
              <p className="text-sm text-slate-500 mt-4">
                We contacteren je zodra de immo-plannen beschikbaar zijn.
              </p>
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50/50 to-blue-50/50 p-5">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <p className="text-sm text-slate-700">
                Het Pro plan bevat een <strong className="text-emerald-700">gratis proefperiode van 4 maanden</strong>. Na de trial start de automatische facturatie. Annuleer op elk moment — geen verplichtingen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── AFSPRAAK BOEKEN ─── */}
      <section id="afspraak" className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-sm font-medium text-[#2B6FA6] mb-6">
              <Phone className="w-4 h-4" />
              Persoonlijke ondersteuning
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Hulp nodig bij de onboarding?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Boek een gratis gesprek en we helpen je op weg. Van account instellen tot je eerste pand online — we staan voor je klaar.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Info cards */}
            <div className="lg:col-span-2 space-y-4">
              {[
                { icon: Clock, title: '30 min gesprek', desc: 'Persoonlijke begeleiding bij de opstart' },
                { icon: Users, title: '1-op-1 met het team', desc: 'Rechtstreeks contact met de oprichter' },
                { icon: Zap, title: 'Direct aan de slag', desc: 'Na het gesprek ben je klaar om te starten' },
                { icon: Gift, title: 'Volledig gratis', desc: 'Geen kosten, geen verplichtingen' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200/80 shadow-sm">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#5EABEB]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{item.title}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Booking form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-lg p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}>
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Boek een afspraak</h3>
                    <p className="text-sm text-slate-500">Vul je gegevens in, wij nemen contact op</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Naam *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        placeholder="Je volledige naam"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="je@email.com"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none placeholder:text-slate-400"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Telefoon</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+32 ..."
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Type verhuurder</label>
                      <select
                        name="landlordType"
                        value={formData.landlordType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none bg-white"
                      >
                        <option value="particulier">Particulier</option>
                        <option value="immokantoor">Immokantoor / Agency</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Aantal panden</label>
                      <select
                        name="propertyCount"
                        value={formData.propertyCount}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none bg-white"
                      >
                        <option value="">Selecteer...</option>
                        <option value="1">1 pand</option>
                        <option value="2-5">2 - 5 panden</option>
                        <option value="6-20">6 - 20 panden</option>
                        <option value="20+">20+ panden</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Voorkeursdatum</label>
                      <input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Bericht (optioneel)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Vertel ons waar je hulp bij nodig hebt..."
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all outline-none resize-none placeholder:text-slate-400"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Afspraak aanvragen
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 sm:py-24" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #F0F7FF 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Klaar om te starten?
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Maak gratis een account aan en probeer StudyStay Pro 4 maanden gratis. Plaats je eerste pand vandaag nog online.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/user-type-login?source=verhuurder-info&userType=landlord&landlordType=private&mode=register"
              className="group inline-flex items-center justify-center px-10 py-4 text-white font-semibold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full sm:w-auto"
              style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
            >
              Registreer gratis
              <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/user-type-login?source=verhuurder-info&userType=landlord"
              className="inline-flex items-center justify-center px-10 py-4 bg-white text-slate-700 font-semibold text-lg rounded-2xl shadow-sm border-2 border-slate-200 hover:border-[#5EABEB] hover:shadow-md transition-all duration-300 w-full sm:w-auto"
            >
              Ik heb al een account
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-slate-200/80 bg-gradient-to-b from-white to-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-12">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
              <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-slate-200/60" style={{ backgroundColor: '#60ADF0' }}>
                    <StudyStayLogo className="w-6 h-6" />
                  </div>
                  <span className="text-lg font-bold text-slate-900">StudyStay</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Het platform dat studenten en verhuurders samenbrengt.
                </p>
                <div className="text-xs text-slate-500 space-y-0.5">
                  <p><strong>Nolf Technologies OÜ</strong></p>
                  <p>Tornimäe tn 5, 10145 Tallinn, Estonia</p>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900">Navigatie</h4>
                <ul className="space-y-2.5">
                  <li><Link to="/welcome" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors">Startpagina</Link></li>
                  <li><Link to="/contact" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors">Contact</Link></li>
                  <li><a href="#afspraak" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors">Afspraak boeken</a></li>
                </ul>
              </div>
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900">Juridisch</h4>
                <ul className="space-y-2.5">
                  <li><Link to="/privacy" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors">Privacybeleid</Link></li>
                  <li><Link to="/terms" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors">Algemene Voorwaarden</Link></li>
                  <li><Link to="/legal" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors">Alle documenten</Link></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200/80 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Nolf Technologies OÜ. Alle rechten voorbehouden.
            </p>
            <p className="text-xs text-slate-400">
              Made with ❤️ for students
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
